import moment from "moment";
import { getNameComponents } from "../../utils/name";
import { splitSuburbStatePostcodeString } from "../../utils";

export type Address = {
  streetAddress: string;
  suburb: string;
  postcode: string;
  state: string;
};

export const emptyAddress: Address = {
  streetAddress: "",
  state: "",
  suburb: "",
  postcode: "",
};

export type Client = {
  id: string | undefined;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phoneNumberHome: string;
  phoneNumberMobile: string;
  dvaCardNumber: string;
  medicareCardNumber: string;
  medicareCardIRN: string;
  addressResidential: Address;
};

export type Clinic = {
  id: string | undefined;
  name: string;
  address: Address;
  phone: string;
  fax: string;
  email: string;
};

export type Referrer = {
  id: string | undefined;
  firstName: string;
  lastName: string;
  providerNumber: string;
  title: string;
  address: Address;
  phone: string;
  mobile: string;
  fax: string;
  email: string;
};

export type Referral = {
  id: string | undefined;
  startDate: string;
  expiredDate: string;
  expiredDateByMonth: string;
};

type File = {
  id: string;
  name: string;
};

export type ManagedIncomingReferral = {
  client: Client;
  clinic: Clinic;
  referrer: Referrer;
  referral: Referral;
  letterFile: File;
  weeksToAppointment: string;
  expectedAppointmentDate: string | null;
  rawReferralId: string;
};

export type RawReferral = {
  address: string;
  caseManager: string;
  clientExisting: string;
  clinic: {
    name: string;
  };
  clientGroup: string;
  clientName: string;
  dateOfBirth: string;
  dvaCardNumber: string;
  email: string;
  fileName: string;
  gender: string;
  location: string;
  medicareCardIRN: string;
  medicareCardNumber: string;
  note1: string;
  note2: string;
  oriFileName: string;
  phoneNumberHome: string;
  phoneNumberMobile: string;
  referrerName: string;
  startDate: string;
  suburbStatePostalCode: string;
  title: string;
  ExternalId: string;
  id?: string;
};

export const rawReferralToManagedIncomingReferral: (rawReferral: RawReferral) => ManagedIncomingReferral = (
  rawReferral
) => {
  const referralStartDateMoment = moment(rawReferral.startDate, "DD/MM/YYYY");
  const referralStartDate = referralStartDateMoment.isValid() ? referralStartDateMoment.format("YYYY-MM-DD") : "";
  const referralEndDate = referralStartDateMoment.isValid()
    ? referralStartDateMoment.add(1, "year").format("YYYY-MM-DD")
    : "";
  const dateOfBirth = moment(rawReferral.dateOfBirth, "DD/MM/YYYY").isValid()
    ? moment(rawReferral.dateOfBirth, "DD/MM/YYYY").format("YYYY-MM-DD")
    : "";
  const { firstName: clientFirstName, lastName: clientLastName } = getNameComponents(rawReferral.clientName);
  const { firstName: referrerFirstName, lastName: referrerLastName } = getNameComponents(rawReferral.referrerName);

  const suburbStatePostalCodeResult = splitSuburbStatePostcodeString(rawReferral.suburbStatePostalCode);
  const addressResidential = suburbStatePostalCodeResult
    ? { ...suburbStatePostalCodeResult, streetAddress: rawReferral.address }
    : {
        ...emptyAddress,
        streetAddress: `${rawReferral.address || ""} ${rawReferral.suburbStatePostalCode || ""}`.trim(),
      };

  return {
    client: {
      id: undefined,
      firstName: clientFirstName,
      lastName: clientLastName,
      dateOfBirth,
      email: rawReferral.email,
      phoneNumberHome: rawReferral.phoneNumberHome,
      phoneNumberMobile: rawReferral.phoneNumberMobile,
      dvaCardNumber: rawReferral.dvaCardNumber,
      medicareCardNumber: rawReferral.medicareCardNumber,
      medicareCardIRN: rawReferral.medicareCardIRN,
      addressResidential,
      ExternalId: rawReferral.ExternalId ?? "",
    },
    clinic: {
      id: undefined,
      name: rawReferral.clinic?.name || rawReferral.clientGroup || "",
      address: emptyAddress,
      phone: "",
      fax: "",
      email: "",
    },
    referrer: {
      id: undefined,
      firstName: referrerFirstName,
      lastName: referrerLastName,
      providerNumber: "",
      title: "",
      address: emptyAddress,
      phone: "",
      mobile: "",
      email: "",
      fax: "",
    },
    referral: {
      id: undefined,
      startDate: referralStartDate,
      expiredDate: referralEndDate,
      expiredDateByMonth: "",
    },
    letterFile: {
      id: rawReferral.id ?? rawReferral.fileName,
      name: rawReferral.fileName,
    },
    weeksToAppointment: "0",
    expectedAppointmentDate: null,
    rawReferralId: rawReferral.id ?? rawReferral.fileName,
  };
};
